import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

const InfluencerCard = ({ influencer, onFavorite, onCompare, isComparing, onProposalClick }) => {
  const navigate = useNavigate();
  const { user, openLoginModal } = useAuth();
  const [isFavorited, setIsFavorited] = useState(influencer?.isFavorited || false);

  const handleFavoriteClick = (e) => {
    e?.stopPropagation();
    if (!user) {
      openLoginModal();
      return;
    }
    setIsFavorited(!isFavorited);
    onFavorite?.(influencer?.id);
  };

  const handleCompareClick = (e) => {
    e?.stopPropagation();
    onCompare?.(influencer?.id);
  };

  const handleCardClick = () => {
    navigate(`/influencer-profile?id=${influencer?.id}`);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000)?.toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000)?.toFixed(1)}K`;
    }
    return num?.toString();
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: 'Instagram',
      tiktok: 'Music',
      youtube: 'Youtube',
      twitter: 'Twitter'
    };
    return icons?.[platform] || 'Globe';
  };

  return (
    <div
      onClick={handleCardClick}
      className={`
        bg-card border rounded-lg overflow-hidden transition-smooth hover:shadow-elevation-2 cursor-pointer
        ${isComparing ? 'border-primary' : 'border-border'}
      `}
    >
      <div className="relative h-48 overflow-hidden bg-muted">
        <Image
          src={influencer?.coverImage}
          alt={influencer?.coverImageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={handleFavoriteClick}
            className="w-8 h-8 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background transition-smooth"
            aria-label={isFavorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
          >
            <Icon
              name="Heart"
              size={16}
              color={isFavorited ? 'var(--color-error)' : 'var(--color-foreground)'}
              className={isFavorited ? 'fill-current' : ''}
            />
          </button>
          <button
            onClick={handleCompareClick}
            className={`
              w-8 h-8 rounded-full flex items-center justify-center transition-smooth
              ${isComparing 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-background/90 backdrop-blur-sm hover:bg-background'
              }
            `}
            aria-label={isComparing ? 'Karşılaştırmadan çıkar' : 'Karşılaştırmaya ekle'}
          >
            <Icon name="GitCompare" size={16} />
          </button>
        </div>
        {influencer?.verified && (
          <div className="absolute top-3 left-3 bg-primary/90 backdrop-blur-sm text-primary-foreground px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium">
            <Icon name="BadgeCheck" size={14} />
            <span>Doğrulanmış</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
              <Image
                src={influencer?.avatar}
                alt={influencer?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            {influencer?.isOnline && (
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-card rounded-full" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate">
              {influencer?.name}
            </h3>
            <p className="text-sm text-muted-foreground truncate">
              @{influencer?.username}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          {influencer?.platforms?.map((platform) => (
            <div
              key={platform}
              className="w-6 h-6 bg-muted rounded-full flex items-center justify-center"
              title={platform}
            >
              <Icon name={getPlatformIcon(platform)} size={14} />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-center gap-1 text-muted-foreground mb-1">
              <Icon name="Users" size={14} />
              <span className="text-xs">Takipçi</span>
            </div>
            <p className="text-lg font-semibold text-foreground">
              {formatNumber(influencer?.followers)}
            </p>
          </div>
          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-center gap-1 text-muted-foreground mb-1">
              <Icon name="TrendingUp" size={14} />
              <span className="text-xs">Etkileşim</span>
            </div>
            <p className="text-lg font-semibold text-foreground">
              %{influencer?.engagementRate}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-4">
          {influencer?.categories?.slice(0, 3)?.map((category, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
            >
              {category}
            </span>
          ))}
          {influencer?.categories?.length > 3 && (
            <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
              +{influencer?.categories?.length - 3}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground">Başlangıç Fiyatı</p>
            <p className="text-lg font-semibold text-foreground">
              ₺{influencer?.startingPrice?.toLocaleString('tr-TR')}
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="Send"
            iconPosition="right"
            onClick={(e) => {
              e?.stopPropagation();
              if (!user) {
                openLoginModal();
                return;
              }
              if (onProposalClick) {
                onProposalClick(influencer);
              }
            }}
          >
            Teklif Gönder
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InfluencerCard;